/**
 * `resolveFieldText` — return a new field object whose label / helpText /
 * placeholder / options[] strings have been swapped for their localized
 * versions (falling back to the local value when a UILM key is empty or
 * not yet published).
 *
 * Lets the entire field-renderer tree stay locale-blind: only the
 * dispatch point (`DynamicField`) calls this, and every leaf renderer
 * keeps reading `field.label`, `field.helpText`, etc. — but those values
 * are now whatever UILM says (or the original text, when UILM is empty).
 */

import { tWithFallback } from './t-with-fallback';

type TFn = (key: string, options?: { defaultValue?: string }) => string;

/**
 * Minimum shape the resolver needs. Matches `TemplateField` in
 * `templates.types.ts` but doesn't import it to keep this helper
 * dependency-light and unit-testable.
 */
interface ResolvableField {
  label: string;
  helpText?: string | null;
  placeholder?: string | null;
  options?: string[] | null;
  uilmLabelKey?: string | null;
  uilmHelpTextKey?: string | null;
  uilmPlaceholderKey?: string | null;
  uilmOptionsKeys?: string[] | null;
}

export function resolveFieldText<F extends ResolvableField>(t: TFn, field: F): F {
  const resolvedOptions = Array.isArray(field.options)
    ? field.options.map((opt, idx) => {
        const optKey = field.uilmOptionsKeys?.[idx];
        return tWithFallback(t, optKey, opt);
      })
    : field.options;

  return {
    ...field,
    label: tWithFallback(t, field.uilmLabelKey, field.label),
    helpText: field.helpText
      ? tWithFallback(t, field.uilmHelpTextKey, field.helpText)
      : field.helpText,
    placeholder: field.placeholder
      ? tWithFallback(t, field.uilmPlaceholderKey, field.placeholder)
      : field.placeholder,
    options: resolvedOptions,
  };
}
